import {
  Children,
  cloneElement,
  type HTMLAttributes,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import {
  breadcrumbsItem,
  breadcrumbsList,
  breadcrumbsRoot,
  breadcrumbsSeparator,
} from '@mage-ui/styled-system/recipes';

export type BreadcrumbsProps = {
  separator?: ReactNode;
  classNames?: {
    root?: string;
    list?: string;
    item?: string;
    separator?: string;
  };
  children?: ReactNode;
};

export const Breadcrumbs = ({
  separator = '/',
  children,
  classNames,
}: BreadcrumbsProps) => {
  return (
    <nav
      aria-label='breadcrumb'
      className={classNames?.root ?? breadcrumbsRoot()}
    >
      <ol className={classNames?.list ?? breadcrumbsList()}>
        {Children.map(children, (child, index) => {
          if (
            isValidElement<{ children: ReactNode }>(child) &&
            child.type === Item
          ) {
            return (
              <li
                key={child.key}
                className={classNames?.item ?? breadcrumbsItem()}
              >
                {index < Children.count(children) - 1 && (
                  <>
                    {child}
                    <span
                      aria-hidden='true'
                      className={
                        classNames?.separator ?? breadcrumbsSeparator()
                      }
                    >
                      {separator}
                    </span>
                  </>
                )}
                {index === Children.count(children) - 1 && lastItem(child)}
              </li>
            );
          }
        })}
      </ol>
    </nav>
  );
};

type Child = ReactElement<{ children?: ReactNode }>;

const lastItem = (child: Child) => {
  if (isValidElement(child) && isValidElement(child.props.children)) {
    const childChildren = child.props.children as Child;
    return cloneElement(childChildren, {
      'aria-current': 'page',
      ...(childChildren.props ?? {}),
    } as HTMLAttributes<HTMLElement>);
  }
  return <span aria-current='page'>{child}</span>;
};

type ItemProps = {
  children: ReactNode;
};

const Item = ({ children }: ItemProps) => {
  return children;
};

Item.displayName = 'Breadcrumbs.Item';
Breadcrumbs.Item = Item;
